import React from 'react';
import { cn } from '@/utils/cn';
import { hero, getPageSeo } from '@/data/content';

const HeroBanner: React.FC = () => {
    const seo = getPageSeo('accueil');
    return (
        <section className="relative pt-32 pb-20 lg:pt-14 lg:pb-32 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-app-primary/10 to-app-primary/5 dark:from-gray-800 dark:to-gray-900 blur-3xl opacity-70 animate-pulse-slow"></div>
                <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-gradient-to-tr from-app-primary/5 to-transparent dark:from-app-primary/10 blur-3xl opacity-50 animate-float"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                    <div className="flex-1 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-app-primary/5 dark:bg-gray-800 border border-app-primary/10 dark:border-gray-700 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <span className="flex h-2 w-2 rounded-full bg-app-primary"></span>
                            <span className="text-sm font-bold text-app-primary tracking-wide uppercase">
                                {hero.badge}
                            </span>
                        </div>

                        <h1 className="sr-only">{seo?.h1 ?? ''}</h1>
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-app-text dark:text-white leading-[1.1] mb-6 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
                            {hero.heading.split(hero.heading_highlight).map((part, i, arr) =>
                                i < arr.length - 1 ? (
                                    <React.Fragment key={i}>
                                        {part}
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-app-primary to-orange-600">
                                            {hero.heading_highlight}
                                        </span>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment key={i}>{part}</React.Fragment>
                                )
                            )}
                        </h2>

                        <p className="text-lg md:text-xl font-medium text-app-muted dark:text-gray-300 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                            {hero.tagline}
                        </p>

                        <div className="mt-12 pt-8 border-t border-app-border dark:border-gray-800 flex flex-wrap items-center justify-center lg:justify-start gap-3 animate-in fade-in duration-1000 delay-500">
                            {hero.trust_badges.map((badge, i) => {
                                const cls = cn(
                                    'flex items-center gap-3 px-4 py-3 rounded-2xl bg-white dark:bg-gray-800 border-2 border-orange-200 dark:border-orange-600/40 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-orange-400 dark:hover:border-orange-500 transition-all duration-300',
                                    badge.link && 'cursor-pointer'
                                );
                                const content = (
                                    <>
                                        <div className="w-10 h-10 rounded-full bg-app-primary/5 dark:bg-gray-700 flex items-center justify-center text-xl shrink-0">
                                            {badge.emoji}
                                        </div>
                                        <div className="text-left">
                                            <div className="font-black text-app-text dark:text-white text-sm leading-tight">
                                                {badge.title}
                                            </div>
                                            <div className="text-xs text-app-muted font-medium">{badge.subtitle}</div>
                                        </div>
                                    </>
                                );
                                if (badge.link) {
                                    const isExternal = badge.link.startsWith('http');
                                    return (
                                        <a
                                            key={i}
                                            href={badge.link}
                                            target={isExternal ? '_blank' : undefined}
                                            rel={isExternal ? 'noopener noreferrer' : undefined}
                                            className={cls}
                                        >
                                            {content}
                                        </a>
                                    );
                                }
                                return (
                                    <div key={i} className={cls}>
                                        {content}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex-1 relative animate-in fade-in zoom-in duration-1000 delay-200">
                        <div className="relative aspect-square max-w-lg mx-auto">
                            <div className="absolute inset-0 rounded-full border-[20px] border-app-primary/5 dark:border-gray-800/50 scale-105 pointer-events-none"></div>

                            <div className="relative w-full h-full rounded-full overflow-hidden shadow-2xl shadow-app-primary/20 border-8 border-white dark:border-gray-900 z-10">
                                <img
                                    src={hero.image}
                                    alt={hero.image_alt}
                                    className="w-full h-full object-cover animate-pulse-slow"
                                    style={{ animationDuration: '20s' }}
                                />
                            </div>

                            <div className="absolute bottom-4 left-0 sm:-left-6 z-20 animate-float origin-left">
                                <div className="scale-[0.75] sm:scale-95 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md p-2 sm:p-3 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none border border-white/50 dark:border-gray-700/50 flex items-center gap-2 sm:gap-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 flex items-center justify-center text-lg sm:text-xl font-bold shrink-0">
                                        ✓
                                    </div>
                                    <div>
                                        <div className="font-black text-app-text dark:text-white text-xs sm:text-sm">
                                            {hero.floating_badge.title}
                                        </div>
                                        <div className="text-[10px] sm:text-xs font-medium text-app-muted">
                                            {hero.floating_badge.subtitle}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="absolute top-4 right-0 sm:-right-6 z-20 animate-float origin-right"
                                style={{ animationDelay: '1.5s' }}
                            >
                                <div className="scale-[0.75] sm:scale-95 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-3 py-2 sm:px-4 sm:py-3 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none border border-white/50 dark:border-gray-700/50 flex items-center gap-2 sm:gap-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 flex items-center justify-center text-lg sm:text-xl font-bold shrink-0">
                                        🌶️
                                    </div>
                                    <div className="text-left">
                                        <div className="font-black text-app-text dark:text-white whitespace-nowrap text-xs sm:text-sm">
                                            {hero.floating_badges[0].title}
                                        </div>
                                        <div className="text-[10px] sm:text-xs font-medium text-app-muted whitespace-nowrap">
                                            {hero.floating_badges[0].subtitle}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="absolute top-1/2 -translate-y-1/2 right-0 sm:-right-6 z-20 animate-float origin-right"
                                style={{ animationDelay: '3s' }}
                            >
                                <div className="scale-[0.75] sm:scale-95 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-3 py-2 sm:px-4 sm:py-3 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none border border-white/50 dark:border-gray-700/50 flex items-center gap-2 sm:gap-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 flex items-center justify-center text-lg sm:text-xl font-bold shrink-0">
                                        🎉
                                    </div>
                                    <div className="text-left">
                                        <div className="font-black text-app-text dark:text-white whitespace-nowrap text-xs sm:text-sm">
                                            {hero.floating_badges[1].title}
                                        </div>
                                        <div className="text-[10px] sm:text-xs font-medium text-app-muted whitespace-nowrap">
                                            {hero.floating_badges[1].subtitle}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroBanner;
